import { G, wrapIndex } from '../core/math';
import * as K from '../physics/constants';
import type { Track } from './generate';

/**
 * Classic two-pass lap-time simulation over the centerline.
 *
 * A backward pass propagates each corner's grip limit upstream under braking; a
 * forward pass propagates traction and power downstream; the elementwise minimum
 * is the fastest speed physically possible at every point on the track.
 *
 * That one array is worth a lot. It gives the ideal lap time, the reference
 * speed to score a player's run against, and — because a braking zone is by
 * definition the stretch where the backward pass was the binding constraint —
 * the exact braking point for every corner, derived rather than hand-placed.
 */

export interface SpeedProfile {
  /** Fastest achievable speed at each centerline index, m/s. */
  vIdeal: Float64Array;
  /** Pure cornering limit, ignoring how you get to or from it. */
  vCorner: Float64Array;
  /**
   * Sum of ds/v around the lap, seconds.
   *
   * A strong reference rather than a proven lower bound. It is solved along the
   * centerline with an allowance for the wider radius the track width permits
   * (see `effectiveCurvature`), not by optimising the racing line itself — that
   * would be a full minimum-lap-time problem. A driver who strings corners
   * together better than the allowance assumes could in principle edge under it.
   */
  idealLapTime: number;
}

/**
 * Curvature of the line a driver can actually take, which is flatter than the
 * centerline's because they have the full width to work with.
 *
 * Solving on raw centerline curvature understates every corner speed, and the
 * resulting "ideal" would be beatable by anyone running a proper line — which
 * makes the headline gap on the results screen meaningless.
 */
export function effectiveCurvature(kappa: number, trackWidth: number): number {
  const k = Math.abs(kappa);
  // r_effective = r + width/2, expressed in curvature.
  return k / (1 + (k * trackWidth) / 2);
}

const MU = K.SURFACES.track.mu;

/**
 * Straight-line braking limit, solved for the weight transfer it causes and
 * capped by the brake actuator.
 *
 * The pedal is deliberately sized below tyre capacity so there is grip left for
 * turning in (see BRAKE_LOCK_HEADROOM), so on clean track it is the pedal that
 * binds, not the rubber. Ignoring that would have the solver place braking
 * points at a deceleration the kart cannot actually produce.
 */
export function maxBrakeDecel(mu = MU): number {
  return Math.min(K.dynamicRearCapacity(mu), K.MAX_BRAKE_FORCE) / K.MASS;
}

/** Traction limit under power; transfer works in your favour here. */
export function maxDriveAccel(mu = MU): number {
  const staticShare = (mu * G * K.A_CG) / K.WHEELBASE;
  return staticShare / Math.max(0.15, 1 - (mu * K.CG_HEIGHT) / K.WHEELBASE);
}

export function topSpeed(): number {
  // Solve power == drag * v by bisection. Trig-free and converges in ~40 steps.
  let lo = 1;
  let hi = 120;
  for (let i = 0; i < 60; i++) {
    const mid = (lo + hi) / 2;
    const resist = (K.DRAG_K * mid * mid + K.ROLLING_RESIST) * mid;
    if (resist < K.ENGINE_POWER) lo = mid;
    else hi = mid;
  }
  return lo;
}

function dragDecel(v: number): number {
  return (K.DRAG_K * v * v + K.ROLLING_RESIST) / K.MASS;
}

/**
 * @param widthAllowance How much track width the line may use. Defaults to the
 *   full width, which is the profile a player racing properly is measured
 *   against. Pass 0 for a strict centerline profile — that is what a bot pinned
 *   to the centerline can actually achieve, and targeting the width-aware
 *   numbers instead sends it straight into the grass.
 */
/**
 * @param mu Either a single grip figure, or one per centerline sample. The
 *   per-sample form is what makes wet patches interesting rather than merely
 *   slow: the solver relocates each braking point to suit the grip actually
 *   available where the braking happens.
 */
export function solveSpeedProfile(
  track: Track,
  mu: number | Float64Array = MU,
  widthAllowance = track.width,
): SpeedProfile {
  const n = track.center.length;
  const ds = track.spacing;
  const k = track.curvature;

  // Grip is read per sample, so the passes brake and accelerate against the
  // figure available at the point they are actually passing through.
  const muAt = (i: number): number => (typeof mu === 'number' ? mu : mu[i]);

  const vTop = topSpeed();

  // --- cornering limit ----------------------------------------------------
  // No aero downforce on a kart, so grip does not rise with speed and this
  // solves directly: v = sqrt(mu * g / kappa). No fixed-point iteration.
  const kEff = new Float64Array(n);
  const vCorner = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    const kappa = effectiveCurvature(k[i], widthAllowance);
    kEff[i] = kappa;
    vCorner[i] = kappa < 1e-6 ? vTop : Math.min(vTop, Math.sqrt((muAt(i) * G) / kappa));
  }

  const v = new Float64Array(n);
  v.set(vCorner);

  /** Longitudinal grip left over after cornering has taken its share. */
  const longitudinalAvailable = (speed: number, kappa: number, i: number): number => {
    const grip = muAt(i) * G;
    const lateral = speed * speed * Math.abs(kappa);
    return Math.sqrt(Math.max(0, grip * grip - lateral * lateral));
  };

  // --- backward pass: how early must braking start ------------------------
  // Three laps of the ring so constraints propagate across the seam.
  for (let pass = 0; pass < 3; pass++) {
    for (let step = n; step >= 1; step--) {
      const i = wrapIndex(step, n);
      const prev = wrapIndex(step - 1, n);
      const decel =
        Math.min(maxBrakeDecel(muAt(i)), longitudinalAvailable(v[i], kEff[i], i)) + dragDecel(v[i]);
      const cand = Math.sqrt(v[i] * v[i] + 2 * decel * ds);
      if (cand < v[prev]) v[prev] = cand;
    }
  }

  // --- forward pass: how fast can you get away -----------------------------
  for (let pass = 0; pass < 3; pass++) {
    for (let step = 0; step < n; step++) {
      const i = wrapIndex(step, n);
      const next = wrapIndex(step + 1, n);
      const powerLimited = K.ENGINE_POWER / (K.MASS * Math.max(v[i], 1));
      const accel =
        Math.min(powerLimited, maxDriveAccel(muAt(i)), longitudinalAvailable(v[i], kEff[i], i)) -
        dragDecel(v[i]);
      if (accel <= 0) continue;
      const cand = Math.sqrt(v[i] * v[i] + 2 * accel * ds);
      if (cand < v[next]) v[next] = cand;
    }
  }

  // --- integrate the lap ---------------------------------------------------
  let idealLapTime = 0;
  for (let i = 0; i < n; i++) {
    const a = v[i];
    const b = v[wrapIndex(i + 1, n)];
    idealLapTime += ds / Math.max(0.5, (a + b) / 2);
  }

  return { vIdeal: v, vCorner, idealLapTime };
}
